import { useEffect } from 'react';

/**
 * Ctrl/⌘ + Shift + S — marca el contenido al que NO SE PUEDE LLEGAR.
 *
 * Nace de haber contestado «ya está todo bien» dos veces y haber fallado las
 * dos. La razón de fondo es que buscaba la forma equivocada.
 *
 * Hay dos fallos de scroll y son opuestos:
 *
 *   · UN SCROLL DE MÁS — la página y una caja de dentro se pelean el gesto.
 *     Ese sí se puede oler en el código: alto fijo más `overflow-y-auto`. Lo
 *     vigila `utils/scrollPagina.test.ts`.
 *
 *   · UN SCROLL QUE FALTA — hay `overflow-y-auto`, pero la caja nunca llega a
 *     tener techo, así que crece con su contenido, el scroll no aparece nunca
 *     y lo que sobra se lo come el recorte de algún padre. En Seguimientos la
 *     columna medía 1618 px dentro de una tarjeta de 761: las últimas
 *     propuestas no existían para el usuario. En el código se ve idéntico al
 *     caso que SÍ funciona —el mismo montaje en flex scrollea y en grid no—,
 *     así que leerlo no basta. Hay que medirlo con la página ya dibujada.
 *
 * Esto mide lo segundo. Para cada trozo de contenido busca quién lo recorta y
 * comprueba si alguien de en medio puede traerlo a la vista. Si nadie puede,
 * lo marca: ese texto está en el DOM y es inalcanzable.
 *
 * Solo en desarrollo. Combínalo con Ctrl/⌘+Shift+D (datos de prueba) para que
 * las listas tengan suficiente contenido como para desbordar.
 */

const ATAJO = 'KeyS';
const ID_ESTILO = 'rp-detector-atrapado';

export interface Atrapado {
    texto: string;
    recortadoPor: string;
    seSaleAbajo: number;
    hayScrollDeclarado: boolean;
}

/** ¿Esta caja recorta lo que se sale? */
function recorta(el: Element): boolean {
    const s = getComputedStyle(el);
    return /hidden|clip|auto|scroll/.test(s.overflowY) || /hidden|clip|auto|scroll/.test(s.overflowX);
}

/** ¿Esta caja puede desplazarse para enseñar lo que le sobra? */
function puedeDesplazarse(el: Element): boolean {
    const s = getComputedStyle(el);
    return /auto|scroll/.test(s.overflowY) && el.scrollHeight - el.clientHeight > 2;
}

/**
 * Exportada para poder probarla: jsdom no calcula layout, pero se le puede dar
 * a mano la geometría exacta del fallo real y comprobar que lo caza.
 */
export function buscarAtrapados(): Atrapado[] {
    const atrapados: Atrapado[] = [];

    for (const el of Array.from(document.querySelectorAll<HTMLElement>('body *'))) {
        const texto = (el.textContent || '').trim();
        // Solo hojas con texto: en un contenedor, lo que se sale es de un hijo
        // y marcarlo también sería ruido.
        if (!texto || el.children.length > 0) continue;

        const s = getComputedStyle(el);
        if (s.display === 'none' || s.visibility === 'hidden') continue;

        const caja = el.getBoundingClientRect();
        if (caja.height === 0) continue;

        // Buscar hacia arriba quién lo recorta, mirando por el camino si alguien
        // puede desplazarse para traerlo.
        for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
            // Alguien puede traerlo a la vista: no está atrapado.
            if (puedeDesplazarse(p)) break;
            if (!recorta(p)) continue;

            const limite = p.getBoundingClientRect();
            const sobra = Math.round(caja.bottom - limite.bottom);
            /* Cabe DENTRO de esta caja, pero se sigue subiendo: puede que a
               quien recorte de verdad sea un abuelo. Es justo lo que pasaba en
               Seguimientos — la lista cabía en su propia caja de scroll (que
               había crecido con el contenido), y quien cortaba era la tarjeta
               blanca dos niveles más arriba. Parar aquí dejaba el fallo sin
               ver, y esta prueba lo cazó antes de que llegara a la app. */
            if (sobra <= 2) continue;

            // Recortado y nadie por el camino puede moverse: inalcanzable.
            el.dataset.rpAtrapado = 'si';
            p.dataset.rpRecorta = 'si';
            atrapados.push({
                texto: texto.slice(0, 60),
                recortadoPor: (typeof p.className === 'string' ? p.className : '').slice(0, 70),
                seSaleAbajo: sobra,
                // Un `overflow-y-auto` que no sirve para nada es la pista de
                // que a la caja le falta techo, no que le falte el scroll.
                hayScrollDeclarado: /auto|scroll/.test(getComputedStyle(p).overflowY),
            });
            break;
        }
    }
    return atrapados;
}

function limpiar() {
    document.getElementById(ID_ESTILO)?.remove();
    for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-rp-atrapado],[data-rp-recorta]'))) {
        delete el.dataset.rpAtrapado;
        delete el.dataset.rpRecorta;
    }
}

export function useDetectorAtrapado() {
    useEffect(() => {
        if (!import.meta.env.DEV) return;

        let encendido = false;

        const alternar = () => {
            if (encendido) {
                limpiar();
                encendido = false;
                console.info('[atrapado] apagado');
                return;
            }

            const atrapados = buscarAtrapados();
            const estilo = document.createElement('style');
            estilo.id = ID_ESTILO;
            estilo.textContent = `
                [data-rp-atrapado] { outline: 2px solid #7c3aed !important; background: rgba(124,58,237,.12) !important; }
                [data-rp-recorta]  { outline: 2px dashed #7c3aed !important; outline-offset: -2px; }
            `;
            document.head.appendChild(estilo);
            encendido = true;

            if (atrapados.length === 0) {
                console.info('[atrapado] no hay contenido inalcanzable en esta pantalla ✓');
            } else {
                console.warn(
                    `[atrapado] ${atrapados.length} trozos de contenido que NADIE puede traer a la vista.\n` +
                    'Morado sólido: el contenido. Morado punteado: quién lo recorta.',
                );
                console.table(atrapados);
            }
        };

        const alTeclado = (e: KeyboardEvent) => {
            if (e.code !== ATAJO || !e.shiftKey || !(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            alternar();
        };

        window.addEventListener('keydown', alTeclado);
        return () => {
            window.removeEventListener('keydown', alTeclado);
            limpiar();
        };
    }, []);
}
